import type { ReactNode } from 'react';
import { cx } from '../cx';

/**
 * Badge / Tag. Also the renderer for SELECT and MULTI_SELECT options, which is
 * why the tone list is a CLOSED set of six token pairs: an option stores a tone
 * id, never a hex colour, so the palette stays editorial and theme-aware.
 */
export const TAG_TONES = ['neutral', 'accent', 'success', 'warning', 'danger', 'info'] as const;
export type TagTone = (typeof TAG_TONES)[number];

export type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  readonly children: ReactNode;
  readonly tone?: TagTone | undefined;
  readonly size?: BadgeSize | undefined;
  /**
   * An action that belongs TO the tag, rendered outside the truncation (D10's token
   * row: a chip that can be removed). Inside it, a long label would cut the control off
   * with an ellipsis - which is the one thing a control must never be.
   */
  readonly trailing?: ReactNode | undefined;
  readonly className?: string | undefined;
}

const TONES: Record<TagTone, string> = {
  neutral: 'bg-tag-neutral-surface text-tag-neutral',
  accent: 'bg-tag-accent-surface text-tag-accent',
  success: 'bg-tag-success-surface text-tag-success',
  warning: 'bg-tag-warning-surface text-tag-warning',
  danger: 'bg-tag-danger-surface text-tag-danger',
  info: 'bg-tag-info-surface text-tag-info',
};

const SIZES: Record<BadgeSize, string> = {
  sm: 'px-1.5 py-0.5 text-2xs',
  md: 'px-2 py-0.5 text-xs',
};

export function Badge({
  children,
  tone = 'neutral',
  size = 'md',
  trailing,
  className,
}: BadgeProps) {
  return (
    <span
      className={cx(
        'inline-flex max-w-full items-center gap-1 rounded-pill font-medium',
        'whitespace-nowrap',
        TONES[tone],
        SIZES[size],
        className,
      )}
    >
      <span className="truncate">{children}</span>
      {trailing !== undefined && <span className="shrink-0">{trailing}</span>}
    </span>
  );
}
